import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import Card from '../UI/Card';
import FormField from '../UI/FormField';
import { IProduct } from '@/interfaces/product.interface';
import React, { useEffect, useState } from 'react';
import ErrorMessage from '@/components/common/ErrorMessage';
import { errorTypes } from '@/utility/validation';
import { showToastNotification, ThrowDateTimeError } from '@/utility/helper';

interface ProductDiscountProps {
  register: UseFormRegister<IProduct>;
  watch: UseFormWatch<IProduct>;
  setValue: UseFormSetValue<IProduct>;
  control: Control<IProduct>;
  error: FieldErrors<IProduct>;
}

const ProductDiscount: React.FC<ProductDiscountProps> = ({
  register,
  watch,
  setValue,
  control,
  error,
}) => {
  const prefix = 'productInfo.0.';
  const discountStartDate = watch(`${prefix}discountStartDate`);
  const target = watch('target');
  const standardDiscount = watch(`${prefix}discountPercentage`);
  const employeeDiscount = watch(`${prefix}meta.employee.discount.value`);

  const discountEndDate = watch(`${prefix}discountEndDate`);

  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  const tabs = [
    { value: 'standard', label: 'Standard' },
    { value: 'employee', label: 'Employee Only' },
  ];

  useEffect(() => {
    if (!discountStartDate || !discountEndDate) {
      setDateError(null);
      return;
    }
    const start = new Date(discountStartDate);
    const end = new Date(discountEndDate);
    setDateError(ThrowDateTimeError(start, end));
  }, [discountStartDate, discountEndDate]);

  useEffect(() => {
    const hasStandardDiscount = [discountStartDate, discountEndDate].some(
      (val) => !!val,
    );

    const hasEmployeeDiscount =
      employeeDiscount != null &&
      !Number.isNaN(employeeDiscount) &&
      employeeDiscount > 0;

    setDiscountEnabled(hasStandardDiscount || hasEmployeeDiscount);
  }, [discountStartDate, discountEndDate, employeeDiscount]);

  useEffect(() => {
    const hasStandardDiscount =
      standardDiscount != null && !Number.isNaN(standardDiscount);

    if (target === 'employee' && hasStandardDiscount) {
      setValue('target', 'standard');

      showToastNotification(
        'warning',
        'Cannot use employee discount while standard discount exists',
      );
    }
  }, [target, standardDiscount]);

  return (
    <>
      {/* Discount */}
      <Card>
        <h2 className="text-lg font-semibold mb-6">Discount</h2>
        <FormField
          label="Add Product Discount"
          type="checkbox"
          checked={discountEnabled}
          onChange={(e) => {
            const checked = (e.target as HTMLInputElement).checked;
            setDiscountEnabled(checked);
            if (!checked) {
              // setValue(`${prefix}discountStartDate`, null);
              // setValue(`${prefix}discountEndDate`, null);
              setValue(`${prefix}discountPercentage`, 0);
            }
          }}
        />
      </Card>
      {discountEnabled && (
        <>
          {/* Discount Value & Eligibility */}

          <div className="grid grid-cols-1  gap-6 mb-6">
            {/* Discount Value */}
            <Card>
              <div className="space-y-6">
                <h2 className="text-lg font-semibold">Discount Value</h2>
                <Controller
                  control={control}
                  name="target"
                  render={({ field }) => (
                    <div className="relative bg-primary-light/30 w-fit p-1 rounded-lg flex items-center text-sm">
                      {/* Sliding Background */}
                      <div
                        className={`absolute top-1 bottom-1 rounded-md bg-white shadow-sm transition-all duration-300 ease-in-out
                          ${target === 'standard' ? 'left-1 w-23' : 'left-24.25 w-30.25'}
                        `}
                      />

                      {tabs.map((i) => {
                        const active = target === i.value;

                        return (
                          <button
                            type="button"
                            key={i.value}
                            onClick={() => field.onChange(i.value)}
                            className={`
                              relative z-10 px-4 py-1.5 rounded-md transition-colors duration-300
                              ${active ? 'text-black font-medium' : 'text-muted-foreground'}
                            `}
                          >
                            {i.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                />
                {target === 'standard' && (
                  <div>
                    <FormField
                      label="Label"
                      placeholder="EID Special"
                      {...register(`${prefix}discountLabel`, {
                        ...errorTypes.required('Discount Label'),
                      })}
                    />
                    <ErrorMessage
                      message={
                        error?.productInfo &&
                        error?.productInfo?.[0]?.discountLabel?.message
                      }
                    />
                  </div>
                )}
                {/* Discount Type + Value */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  <FormField label="Discount Type" value={'percentage'} />
                  {target === 'standard' && (
                    <div>
                      <FormField
                        label="Value"
                        type="number"
                        placeholder="20"
                        suffix="%"
                        step="0.01"
                        min={0.01}
                        onWheel={(e) => e.currentTarget.blur()}
                        {...register(`${prefix}discountPercentage`, {
                          setValueAs: (value) => {
                            if (value === '' || value == null) {
                              return null;
                            }

                            const parsed = parseFloat(value);

                            return Number.isNaN(parsed) ? null : parsed;
                          },
                          ...errorTypes.required('Discount value'),
                        })}
                      />
                      <ErrorMessage
                        message={
                          error?.productInfo &&
                          error?.productInfo?.[0]?.discountPercentage?.message
                        }
                      />
                    </div>
                  )}
                  {target === 'employee' && (
                    <div>
                      <FormField
                        label="Value"
                        type="number"
                        placeholder="20"
                        suffix="%"
                        step="0.01"
                        min={0.01}
                        onWheel={(e) => e.currentTarget.blur()}
                        {...register(`${prefix}meta.employee.discount.value`, {
                          setValueAs: (value) => {
                            if (value === '' || value == null) {
                              return null;
                            }

                            const parsed = parseFloat(value);

                            return Number.isNaN(parsed) ? null : parsed;
                          },
                        })}
                      />
                      <ErrorMessage
                        message={
                          error?.productInfo &&
                          error?.productInfo?.[0]?.discountPercentage?.message
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {target === 'standard' && (
            <Card>
              <div className="space-y-6">
                <h2 className="text-lg font-semibold mb-4">Active dates</h2>
                <Controller
                  name={`${prefix}discountStartDate`}
                  control={control}
                  render={({ field }) => (
                    <div className="grid grid-cols-2 gap-2">
                      <FormField
                        label="Start Date"
                        type="date"
                        value={field.value ? field.value.split('T')[0] : ''}
                        onChange={(e) => {
                          const timePart =
                            field.value?.split('T')[1] || '00:00';
                          field.onChange(`${e.target.value}T${timePart}`);
                        }}
                        required
                        className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                      />
                      <FormField
                        label="Start Time"
                        type="time"
                        value={
                          field.value
                            ? field.value.split('T')[1].slice(0, 5)
                            : ''
                        }
                        onChange={(e) => {
                          const datePart =
                            field.value?.split('T')[0] ||
                            new Date().toISOString().split('T')[0];
                          field.onChange(`${datePart}T${e.target.value}`);
                        }}
                        className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                      />
                    </div>
                  )}
                />
                <Controller
                  name={`${prefix}discountEndDate`}
                  control={control}
                  render={({ field }) => (
                    <>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {/* Date */}
                        <FormField
                          label="End Date"
                          type="date"
                          required
                          value={field.value ? field.value.split('T')[0] : ''}
                          onChange={(e) => {
                            const timePart =
                              field.value?.split('T')[1] || '00:00';
                            field.onChange(`${e.target.value}T${timePart}`);
                          }}
                          className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                        />
                        {/* Time */}
                        <FormField
                          label="End Time"
                          type="time"
                          value={
                            field.value
                              ? field.value.split('T')[1].slice(0, 5)
                              : ''
                          }
                          onChange={(e) => {
                            const datePart =
                              field.value?.split('T')[0] ||
                              new Date().toISOString().split('T')[0];
                            field.onChange(`${datePart}T${e.target.value}`);
                          }}
                          className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                        />
                      </div>
                    </>
                  )}
                />
                <ErrorMessage message={dateError as string} />
              </div>
            </Card>
          )}
        </>
      )}
    </>
  );
};

export default ProductDiscount;
